/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
/*Voltage range will be limited by one of the stages in the filter.  It can be limited three ways:
- The input signal is beyond the op amp's input range.  
- The output signal is beyond the op amp's output range
- The signal exceeds the slew rate the op amp can offer
For each stage we determine a "multiplier" for these three properties.  We then look at all the multipliers from all the stages.  The smallest one sets the voltage range.  
How we determine multipliers:
- Input:  Look at magnitude of the signal coming into the stage (this will be an array of points vs. frequency).  Pick the biggest point.
          Divide the input range of that stage by that biggest point
- Output: Look at magnitude of the signal coming out of the stage (this will be an array of points vs. frequency).  Pick the biggest point.
          Divide the output range of the stage by that biggest point
- Slew Rate: Look at the magnitude of the signal coming of the stage.
          For each point, compute product of: magnitude, frequency, 2, PI
          Get the biggest point from the computation above
          Divide slew rate by that biggest point

Peak to peak input voltage range is then just: 
smallest Multiplier * 2 

instantiated by: 
    \models\results\component-selection.js 
    \models\results\tolerance.js 
    \models\optimization\voltage-range.js

We have 7 voltage range files total:
2 for voltage range optimization:
    \optimization\voltage-range.js: main optimization file.  However calls the view based optimization files
    \utils\voltage-range.js: lower level functions used by \optimization\voltage-range.js, not used by view files
5 for the voltage range view:
    \results\sub-results\voltage-range.js: does the calculations that require all of the stages (this file)
    \results\sub-results\voltage-range\voltage-range-computations-per-stage.js: does the lower level calculations per stage
    \results\sub-results\voltage-range-detail.js: does the higher level calculations per stage (these functions called directly by our templates)
    \results\sub-results\voltage-range\rate.js: trivial file to display slew rate
    \results\sub-results\voltage-range\signal.js: trivial file to display input and output range
If we do some refactoring in the future, a better organization would be 4 files:
- one file for voltage range optimization (ember computed object)
- one file for voltage range view (ember computed object)
- one file for lower level calculations for all stages (pure javascript code) - called by both view & optimization
- one file for lower level calculations per stage (pure javascript code) - called by all stage javascript functions

*/
import EmberObject, { computed } from '@ember/object';

import VoltageRangeDetail from './voltage-range-detail';
import VoltageRangeComputationPerStage from './voltage-range/voltage-range-computations-per-stage';

// Set the following constant to true here to collect voltage-range data from the log
// Also see REDUCE_GRANULARITY_FOR_TEST_DATA_COLLECTION in app/models/process-data/frequency to drop the data collected from 262 points to 10 points
const COLLECT_VOLTAGE_RANGE_DATA = false;

const insertBetween = function (string, pattern1, pattern2, insertion) {
  let i = string.indexOf(pattern1 + pattern2);
  return i < 0 ? string : string.substring(0, i + pattern1.length) + insertion + string.substring(i + pattern1.length);
};

const formatStage = function (stage) {
  const CRLF = '\r\n';
  const IND = '  ';
  const IND1 = CRLF + IND;
  const IND2 = IND1 + IND;
  const IND3 = IND2 + IND;

  let logtext = JSON.stringify(stage) + '$$$';
  logtext = insertBetween(logtext, '[', '{', CRLF);

  logtext = insertBetween(logtext, '{', '"input":{', IND1);
  logtext = insertBetween(logtext, '"input":{', '"simCircuit"', IND2);
  logtext = insertBetween(logtext, ',', '"components":', IND2);

  logtext = insertBetween(logtext, '"components":{', '"opAmp":', IND3);
  logtext = insertBetween(logtext, ',', '"c1":', IND3);
  logtext = insertBetween(logtext, ',', '"r1":', IND3);
  logtext = insertBetween(logtext, ',', '"l1":', IND3);

  logtext = insertBetween(logtext, ',', '"maxFrequency":', IND2);
  logtext = insertBetween(logtext, ',', '"frequencies":', IND2);
  logtext = insertBetween(logtext, ',', '"inputVoltages":', IND2);

  logtext = insertBetween(logtext, '', '},"intermediate":{', IND1);
  logtext = insertBetween(logtext, ',', '"intermediate":{', IND1);

  logtext = insertBetween(logtext, '"intermediate":{', '"outputVoltages"', IND2);

  logtext = insertBetween(logtext, '', '},"output":{', IND1);
  logtext = insertBetween(logtext, ',', '"output":{', IND1);

  logtext = insertBetween(logtext, '"output":{', '"amplifierSpecs":', IND2);
  logtext = insertBetween(logtext, '"amplifierSpecs":{', '"inputRange":', IND3);
  logtext = insertBetween(logtext, '},', '"outputRange":', IND3);
  logtext = insertBetween(logtext, '},', '"slewRate":', IND3);
  logtext = insertBetween(logtext, '}', '},"maxSignals":', IND2);
  logtext = insertBetween(logtext, ',', '"maxSignals":', IND2);
  logtext = insertBetween(logtext, '"maxSignals":{', '"inputRange":', IND3);
  logtext = insertBetween(logtext, '},', '"outputRange":', IND3);
  logtext = insertBetween(logtext, '},', '"slewRate":', IND3);
  logtext = insertBetween(logtext, '}', '},"maxGainAllowed":', IND2);
  logtext = insertBetween(logtext, ',', '"maxGainAllowed":', IND2);
  logtext = insertBetween(logtext, ',', '"maxInputSignal":', IND2);

  logtext = insertBetween(logtext, '', '}}$$$', IND1);

  logtext = insertBetween(logtext, '', '}$$$', CRLF);

  return logtext.substring(0, logtext.length - 3);
};

const logVoltageRange = function (result, vRanges) {
  let data = result.map((d, index) => {
    let v = vRanges[index],
      o = v._opAmp;
    const stage = {
      input: {
        simCircuit: v.circuitResult.actualStage.selectedImplementation + v.circuitResult.actualStage.filter.name,
        components: {
          opAmp: {
            inHeadroomMinus: o.inHeadroomMinus,
            inHeadroomPlus: o.inHeadroomPlus,
            name: o.partNumber,
            outHeadroomMinus: o.outHeadroomMinus,
            outHeadroomPlus: o.outHeadroomPlus,
            slewRate: o.slewRate,
            gbw: o.gbw,
            rOut: o.rOut,
          },
          c1: v.circuit.c1Value || 0,
          c2: v.circuit.c2Value || 0,
          c5: v.circuit.c5Value || 0,
          r1: v.circuit.r1Value || 0,
          r2: v.circuit.r2Value || 0,
          r3: v.circuit.r3Value || 0,
          r4: v.circuit.r4Value || 0,
          r5: v.circuit.r5Value || 0,
          r6: v.circuit.r6Value || 0,
          l1: v.circuit.l1Value || 0,
          l2: v.circuit.l2Value || 0,
        },
        maxFrequency: v._maxFrequency,
        frequencies: v.frequencyArray,
        inputVoltages: v._inputMagnitudeArray,
      },
      intermediate: {
        outputVoltages: v.outputMagnitudeArray,
      },
      output: {
        amplifierSpecs: {
          inputRange: d.nominalVoltageDisplay.amplifierSpecs.inputRange,
          outputRange: d.nominalVoltageDisplay.amplifierSpecs.outputRange,
          slewRate: d.nominalVoltageDisplay.amplifierSpecs.slewRate,
        },
        maxSignals: {
          inputRange: d.nominalVoltageDisplay.maxSignals.inputRange,
          outputRange: d.nominalVoltageDisplay.maxSignals.outputRange,
          slewRate: d.nominalVoltageDisplay.maxSignals.slewRate,
        },
        maxGainAllowed: d.nominalVoltageDisplay.smallestMultiplier,
        maxInputSignal: d.nominalVoltageDisplay.maxInputSignal,
      },
    };
    return formatStage(stage);
  });

  console.log('[' + '\r\n' + data.join(',\r\n') + '\r\n' + ']');
};

export default class VoltageRange extends EmberObject {
  circuitResults = null;
  toleranceInput = null; //used by figure-voltage-range.hbs to determine whether to show worst case specs
  vsPlus = null;
  vsMinus = null;
  //in tolerance tab, each stage has a worst case magnitude envelope, defined by a min array and max array.  worstCaseMagnitudes is the max array for each stage
  worstCaseMagnitudes = null;
  whoAmI = 'app/models/results/sub-results/voltage-range.js';
  init() {
    //Dummy code to allow setting a breakpoint during init.  This allows us to find out what instantiated the object (by looking at the call stack.)
    //var whoCreatedMe = "";
  }

  //Generate the object that does the voltage range computations
  @computed('vsMinus', 'vsPlus', 'circuitResults', 'worstCaseMagnitudes') get vRanges() {
    var vsMinus = this.vsMinus;
    var vsPlus = this.vsPlus;
    var circuitResults = this.circuitResults;
    return this._createVoltageRangeComputations(vsMinus, vsPlus, circuitResults, null);
  }

  //Generate the object that does the voltage range computations for worst case
  @computed('vsMinus', 'vsPlus', 'circuitResults', 'worstCaseMagnitudes') get vRangesWorstCase() {
    var worstCaseMagnitudes = this.worstCaseMagnitudes;
    var vsMinus = this.vsMinus;
    var vsPlus = this.vsPlus;
    var circuitResults = this.circuitResults;
    return this._createVoltageRangeComputations(vsMinus, vsPlus, circuitResults, worstCaseMagnitudes);
  }

  //Generate the object that contains what we'll display to the screen
  @computed('vsMinus', 'vsPlus', 'circuitResults', 'vRanges', 'vRangesWorstCase') get detailsOfVoltageRange() {
    var vsMinus = this.vsMinus;
    var vsPlus = this.vsPlus;
    var circuitResults = this.circuitResults;
    var vRanges = this.vRanges;
    var vRangesWorstCase = this.vRangesWorstCase;
    var smallestMultiplier = this._computeSmallestMultiplier(vRanges);
    if (vRangesWorstCase !== null) {
      var smallestMultiplierWorstCase = this._computeSmallestMultiplier(vRangesWorstCase);
    }
    var result = circuitResults.map((circuitResult, index) => {
      var nominalVoltageDisplay = VoltageRangeDetail.create({
        stage: circuitResult.actualStage,
        smallestMultiplier: smallestMultiplier,
        vsMinus: vsMinus,
        vsPlus: vsPlus,
        vRange: vRanges[index],
      });
      //only generate our worst case display details for tolerance/next steps, not component selection
      if (vRangesWorstCase[0] !== null) {
        var worstCaseVoltageDisplay = VoltageRangeDetail.create({
          stage: circuitResult.actualStage,
          smallestMultiplier: smallestMultiplierWorstCase,
          vsMinus: vsMinus,
          vsPlus: vsPlus,
          vRange: vRangesWorstCase[index],
        });
      }
      return { nominalVoltageDisplay: nominalVoltageDisplay, worstCaseVoltageDisplay: worstCaseVoltageDisplay };
    });

    if (COLLECT_VOLTAGE_RANGE_DATA) {
      logVoltageRange(result, vRanges);
    }
    return result;
  }

  //called by both figure-voltage-range.hbs as well as our voltage range optimization function
  //This is the number we display in the upper left of voltage range view.
  //Called for both components tab as well as the nominal value in the tolerances tab
  @computed('detailsOfVoltageRange') get maxInputSignalPossible() {
    var detailsOfVoltageRange = this.detailsOfVoltageRange;
    var result = detailsOfVoltageRange[0].nominalVoltageDisplay.maxInputSignal;
    return result;
  }

  //exactly the same as maxInputSignalPossible, except it's a worst case number for the tolerances tab
  @computed('detailsOfVoltageRange') get worstCaseInputSignal() {
    var detailsOfVoltageRange = this.detailsOfVoltageRange;
    var result = detailsOfVoltageRange[0].worstCaseVoltageDisplay.maxInputSignal;
    return result;
  }

  //instantiate an array of voltage-range-detail objects - one per stage
  _createVoltageRangeComputations(vsMinus, vsPlus, circuitResults, worstCaseMagnitudes) {
    var previousStageOutputMagnitude = null;
    var result = circuitResults.map((circuitResult, index) => {
      //will only have worstCaseMagnitudes in tolerance/next steps, not component selection
      if (worstCaseMagnitudes === null) {
        var worstCaseMagnitude = null;
      } else {
        worstCaseMagnitude = worstCaseMagnitudes[index];
      }
      var voltageRangeStage = VoltageRangeComputationPerStage.create({
        vsMinus: vsMinus,
        vsPlus: vsPlus,
        circuitResult: circuitResult,
        previousStageOutputMagnitude: previousStageOutputMagnitude,
        worstCaseMagnitude: worstCaseMagnitude,
      });
      previousStageOutputMagnitude = voltageRangeStage.outputMagnitudeArray;
      return voltageRangeStage;
    });
    return result;
  }

  //Look at input, output, and slew rate limits for all of our stages.  Pick the one that limits the voltage range the most
  _computeSmallestMultiplier(vRanges) {
    var i;
    var inputMultipliers = [],
      outputMultipliers = [],
      slewRateMultipliers = [];
    for (i = 0; i < vRanges.length; i++) {
      inputMultipliers[i] = vRanges[i].multiplierSignalInput;
      outputMultipliers[i] = vRanges[i].multiplierSignalOutput;
      slewRateMultipliers[i] = vRanges[i].multiplierSignalSlewRate;
    }
    var minimumInputMultiplier = Math.min.apply(Math, inputMultipliers);
    var minimumOutputMultiplier = Math.min.apply(Math, outputMultipliers);
    var minimumSlewRateMultiplier = Math.min.apply(Math, slewRateMultipliers);
    var result = Math.min(minimumInputMultiplier, minimumOutputMultiplier, minimumSlewRateMultiplier);

    return result;
  }
}
